import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import api from '@/lib/api';

const PermContext = createContext({});

const ALL_PAGES = ['dashboard','apteka_control','deliveries','payments','products','inventory','catalog','warehouse','district_summary','growth','month_closing','settings','company_catalog','diary','notifications','messages','share_code_analytics','mr_dashboard'];

const DEFAULT_PAGES = {
  platform_owner: ALL_PAGES,
  platform_admin: ALL_PAGES,
  super_admin: ALL_PAGES,
  company_owner: ALL_PAGES,
  company_admin: ALL_PAGES,
  admin: ALL_PAGES,
  manager: ['dashboard','apteka_control','deliveries','payments','products','catalog','district_summary','growth','notifications','messages'],
  mr: ['dashboard','apteka_control','deliveries','payments','products','catalog','district_summary','growth','notifications','messages','mr_dashboard'],
  pharmacy_owner: ['dashboard','apteka_control','company_catalog','notifications','messages'],
  pharmacy_admin: ['dashboard','apteka_control','company_catalog','notifications','messages'],
  apteka: ['dashboard','apteka_control','company_catalog','notifications','messages'],
  user: ['dashboard','notifications','messages'],
};

export function PermissionsProvider({ children }) {
  const { user } = useAuth();
  const [pagePerms, setPagePerms] = useState(DEFAULT_PAGES);
  const [granularPerms, setGranularPerms] = useState({});
  const [version, setVersion] = useState(0);

  const loadPerms = useCallback(() => {
    if (!user) return;
    api.get('/role-permissions').then(r => {
      if (r.data && Object.keys(r.data).length > 0) {
        const pages = {};
        const granular = {};
        for (const [role, data] of Object.entries(r.data)) {
          if (data.pages) {
            // New format: {pages: [...], permissions: [...]}
            pages[role] = data.pages;
            granular[role] = data.permissions || [];
          } else if (Array.isArray(data)) {
            // Legacy format: [page_keys]
            pages[role] = data;
          }
        }
        setPagePerms(prev => ({ ...prev, ...pages }));
        setGranularPerms(granular);
      }
    }).catch(() => {});
  }, [user]);

  useEffect(() => { loadPerms(); }, [loadPerms, version]);

  const PLATFORM_ROLES = ['platform_owner', 'platform_admin', 'super_admin'];
  
  const hasAccess = (permKey) => {
    if (!user) return false;
    if (PLATFORM_ROLES.includes(user.role) || PLATFORM_ROLES.includes(user._realRole)) return true;
    const role = user.role || 'user';
    const allowed = pagePerms[role] || DEFAULT_PAGES[role] || ['dashboard'];
    return allowed.includes(permKey);
  };

  const hasPermission = (permKey) => {
    if (!user) return false;
    if (PLATFORM_ROLES.includes(user.role) || PLATFORM_ROLES.includes(user._realRole)) return true;
    const role = user.role || 'user';
    const perms = granularPerms[role] || [];
    return perms.includes(permKey);
  };

  // Call this after saving permissions to refresh
  const refreshPerms = () => setVersion(v => v + 1);

  return (
    <PermContext.Provider value={{ perms: pagePerms, hasAccess, hasPermission, refreshPerms }}>
      {children}
    </PermContext.Provider>
  );
}

export const usePermissions = () => useContext(PermContext);
